import { TAB_AS_NBSP } from '@root/helpers/editor';

// mammoth renders footnote/endnote references as <sup> anchors and appends the note
// bodies as a trailing list; clause wording must exclude both.
const NOTE_BODY_SELECTOR = 'li[id^="footnote-"], li[id^="endnote-"]';
const NOTE_REF_SELECTOR = 'a[href^="#footnote-"], a[href^="#endnote-"]';

export const stripNoteContent = (doc: Document): void => {
  doc.body.querySelectorAll(NOTE_BODY_SELECTOR).forEach((noteItem) => {
    const list = noteItem.parentElement;
    noteItem.remove();

    if (list && (list.tagName === 'OL' || list.tagName === 'UL') && list.children.length === 0) {
      list.remove();
    }
  });

  doc.body.querySelectorAll(NOTE_REF_SELECTOR).forEach((reference) => {
    (reference.closest('sup') ?? reference).remove();
  });
};

export const stripTextColors = (doc: Document): void => {
  doc.body.querySelectorAll<HTMLElement>('span[style*="color"]').forEach((span) => {
    const remaining = (span.getAttribute('style') ?? '')
      .split(';')
      .map((declaration) => declaration.trim())
      .filter((declaration) => declaration && !/^color\s*:/i.test(declaration))
      .join('; ');

    if (remaining) {
      span.setAttribute('style', remaining);
    } else {
      span.removeAttribute('style');
    }

    if (span.attributes.length === 0) {
      span.replaceWith(...span.childNodes);
    }
  });
};

const ALLOWED_FONT_SIZES_PT = [7, 9, 11, 13, 15];
const DEFAULT_FONT_SIZE_INDEX = ALLOWED_FONT_SIZES_PT.indexOf(9);

const nearestAllowedSize = (size: number): number =>
  ALLOWED_FONT_SIZES_PT.reduce((best, allowed) => (Math.abs(allowed - size) < Math.abs(best - size) ? allowed : best));

// When explicit sizes cover a minority of the text, the dominant size is likely heading
// text, not body — then sizes snap to the nearest allowed instead of rebasing around 9pt.
export const normaliseFontSizes = (doc: Document): void => {
  const sized = Array.from(doc.body.querySelectorAll<HTMLElement>('[style*="font-size"]'));
  const weights = new Map<number, number>();

  sized.forEach((element) => {
    const size = parseFloat(element.style.fontSize);

    if (element.style.fontSize.endsWith('pt') && Number.isFinite(size)) {
      weights.set(size, (weights.get(size) ?? 0) + (element.textContent?.length ?? 0));
    }
  });

  if (weights.size === 0) {
    return;
  }

  const sizedLength = [...weights.values()].reduce((sum, length) => sum + length, 0);
  const shouldRebase = sizedLength * 2 >= (doc.body.textContent?.length ?? 0);
  const mapping = new Map<number, number>();

  if (shouldRebase) {
    const bodySize = [...weights.entries()].reduce((body, candidate) =>
      candidate[1] > body[1] || (candidate[1] === body[1] && candidate[0] > body[0]) ? candidate : body,
    )[0];
    const sortedSizes = [...weights.keys()].sort((a, b) => a - b);
    const bodyIndex = sortedSizes.indexOf(bodySize);

    sortedSizes.forEach((size, index) => {
      const allowedIndex = Math.min(
        ALLOWED_FONT_SIZES_PT.length - 1,
        Math.max(0, DEFAULT_FONT_SIZE_INDEX + (index - bodyIndex)),
      );
      mapping.set(size, ALLOWED_FONT_SIZES_PT[allowedIndex]);
    });
  } else {
    weights.forEach((_, size) => mapping.set(size, nearestAllowedSize(size)));
  }

  sized.forEach((element) => {
    const mapped = mapping.get(parseFloat(element.style.fontSize));

    if (mapped === undefined) {
      return;
    }

    element.style.fontSize = mapped === ALLOWED_FONT_SIZES_PT[DEFAULT_FONT_SIZE_INDEX] ? '' : `${mapped}pt`;

    if (!element.getAttribute('style')) {
      element.removeAttribute('style');
    }

    if (element.tagName === 'SPAN' && element.attributes.length === 0) {
      element.replaceWith(...element.childNodes);
    }
  });
};

// The editor schema has no image node, so tiptap would silently delete these on paste;
// stripping them here lets us count and report the loss instead.
export const stripUnsupportedElements = (doc: Document): number => {
  const images = doc.body.querySelectorAll('img');

  images.forEach((image) => image.remove());

  return images.length;
};

const isEmptyParagraph = (element: Element | null): element is Element =>
  element?.tagName === 'P' && !element.textContent?.trim();

// Word documents routinely end with a run of blank padding paragraphs; blank lines
// *between* clauses are meaningful and stay.
export const trimEdgeEmptyParagraphs = (doc: Document): void => {
  while (isEmptyParagraph(doc.body.firstElementChild)) {
    doc.body.firstElementChild.remove();
  }

  while (isEmptyParagraph(doc.body.lastElementChild)) {
    doc.body.lastElementChild.remove();
  }
};

// The first tab in a hanging paragraph separates the marker from the text column one indent
// level to its right; two NBSPs approximate that fixed gap — wide markers like (iii) still drift.
const MARKER_GAP = '  ';

const textNodesOf = (element: Node, document: Document): Text[] => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    nodes.push(node as Text);
  }

  return nodes;
};

const replaceFirstTab = (element: Element, document: Document) => {
  for (const node of textNodesOf(element, document)) {
    const text = node.nodeValue ?? '';
    const tabIndex = text.indexOf('\t');

    if (tabIndex >= 0) {
      node.nodeValue = text.slice(0, tabIndex) + MARKER_GAP + text.slice(tabIndex + 1);
      return;
    }
  }
};

export const replaceTabsWithNbsp = (doc: Document): void => {
  if (!doc.body.textContent?.includes('\t')) {
    return;
  }

  doc.body.querySelectorAll('[hanging]').forEach((paragraph) => replaceFirstTab(paragraph, doc));

  textNodesOf(doc.body, doc).forEach((node) => {
    node.nodeValue = (node.nodeValue ?? '').replaceAll('\t', TAB_AS_NBSP);
  });
};
